"use client";

import {
  Button,
  FormPrice,
  FormRoot,
  FormSelect,
  FormTextarea,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeftIcon } from "lucide-react";
import { useEffect } from "react";
import { z } from "zod";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { WalletAccountItem } from "@/modules/wallet/presentation/api/client";
import { formatPrice } from "@/shared/lib/format-price";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import { walletApi } from "../api/client";
import { useCreatePayoutMutation } from "../api/payout-queries";

const CURRENCY_OPTIONS = [
  { value: "LAK", label: "LAK" },
  { value: "THB", label: "THB" },
  { value: "USD", label: "USD" },
];

const PAYOUT_METHOD_OPTIONS = [
  { value: "bank_transfer", label: "ໂອນທະນາຄານ" },
  { value: "cash", label: "ເງິນສົດ" },
];

const OWNER_TYPE_LABELS: Record<string, string> = {
  shop: "ຮ້ານຄ້າ",
  mechanic: "ຊ່າງ",
};

function walletOptionLabel(w: WalletAccountItem): string {
  const type = OWNER_TYPE_LABELS[w.ownerType] ?? w.ownerType;
  const name = w.ownerName ?? w.ownerId ?? w.id.slice(0, 8);
  return `${name} (${type}) · ${formatPrice(w.balance, w.currency)}`;
}

const PayoutCreateSchema = z.object({
  walletId: z
    .string()
    .min(1, "ກະລຸນາເລື່ອກກະເປົາເງິນ")
    .refine((v) => /^[0-9a-f-]{36}$/i.test(v), {
      message: "ກະລຸນາເລື່ອກກະເປົາ (ຮ້ານ/ຊ່າງ)",
    }),
  amount: z.coerce.number().min(0.01, "ຈຳນວນຕ້ອງບໍ່ຕ່ຳກວ່າ 0.01"),
  currency: z.enum(["LAK", "THB", "USD"]),
  payoutMethod: z.enum(["bank_transfer", "cash"]),
  payoutDestinationJson: z.string().optional(),
});

type PayoutCreateValues = z.infer<typeof PayoutCreateSchema>;

const LIMIT = 20;

function payoutWalletLookupQueryFn(args: {
  search: string;
  pageParam: number;
}): Promise<{ items: WalletAccountItem[]; nextPage: number | null }> {
  const offset = (args.pageParam - 1) * LIMIT;
  const baseFilters: { field: string; op: string; value: string | string[] }[] =
    [{ field: "ownerType", op: "in", value: ["shop", "mechanic"] }];
  if (args.search) {
    baseFilters.push({
      field: "ownerId",
      op: "contains",
      value: args.search,
    });
  }
  return walletApi
    .listAccounts({ limit: LIMIT, offset, filters: baseFilters })
    .then((r) => ({
      items: r.data,
      nextPage:
        offset + r.data.length < r.meta.total ? args.pageParam + 1 : null,
    }));
}

function walletPreloadQueryFn(id: string): Promise<WalletAccountItem | null> {
  return walletApi
    .listAccounts({
      limit: 1,
      offset: 0,
      filters: [{ field: "id", op: "eq", value: id }],
    })
    .then((r) => r.data[0] ?? null);
}

export function PayoutCreatePage() {
  const nav = useNavigate({ from: "/app/wallet/payouts/create" });
  const createMutation = useCreatePayoutMutation();

  const methods = RHF.useForm<PayoutCreateValues>({
    resolver: zodResolver(
      PayoutCreateSchema,
    ) as RHF.Resolver<PayoutCreateValues>,
    defaultValues: {
      walletId: "",
      amount: 0,
      currency: "LAK",
      payoutMethod: "bank_transfer",
      payoutDestinationJson: "",
    },
  });

  const selectedWalletId = RHF.useWatch({
    control: methods.control,
    name: "walletId",
    defaultValue: "",
  });

  useEffect(() => {
    if (!selectedWalletId) return;
    walletPreloadQueryFn(selectedWalletId).then((w) => {
      if (w) methods.setValue("currency", w.currency as "LAK" | "THB" | "USD");
    });
  }, [selectedWalletId, methods]);

  const onSubmit = async (vals: PayoutCreateValues) => {
    try {
      let destination: Record<string, unknown> | undefined;
      if (vals.payoutDestinationJson?.trim()) {
        try {
          destination = JSON.parse(vals.payoutDestinationJson.trim()) as Record<
            string,
            unknown
          >;
        } catch {
          // ignore invalid JSON
        }
      }
      const result = await createMutation.mutateAsync({
        walletId: vals.walletId,
        amount: vals.amount,
        currency: vals.currency,
        payoutMethod: vals.payoutMethod,
        payoutDestination: destination,
      });
      nav({ to: "/app/wallet/payouts/$id", params: { id: result.id } });
    } catch {
      // Error handled by mutation
    }
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ກະເປົາເງິນ & ການຈ່າຍເງິນ", to: "/app/wallet" },
            { label: "ການຈ່າຍເງິນອອກ", to: "/app/wallet/payouts" },
            { label: "ສ້າງ" },
          ]}
        />
      </Header>

      <Main>
        <div className="flex flex-wrap items-center justify-between space-y-2">
          <div>
            <h2 className="font-bold text-2xl tracking-tight">ສ້າງຄຳຂໍຈ່າຍເງິນ</h2>
            <p className="text-muted-foreground">
              ເລື່ອກກະເປົາ (ຮ້ານ/ຊ່າງ) ຈຳນວນ ແລະ ວິທີຈ່າຍ
            </p>
          </div>

          <Button
            variant="outline"
            onClick={() => nav({ to: "/app/wallet/payouts" })}
          >
            <ArrowLeftIcon className="size-4" />
            ກັບຄືນ
          </Button>
        </div>

        <div className="mt-6 rounded-xl border bg-card p-6">
          <FormRoot<PayoutCreateValues>
            methods={methods}
            onSubmit={onSubmit}
            className="space-y-4"
          >
            <FormInfiniteCombobox<WalletAccountItem>
              name="walletId"
              label="ກະເປົາເງິນ (ຮ້ານ ຫຼື ຊ່າງ ທີ່ຈະຮັບເງິນ)"
              requiredMark
              queryKey={["wallet", "payout", "accounts", "lookup"]}
              queryFn={payoutWalletLookupQueryFn}
              preloadQueryFn={walletPreloadQueryFn}
              getLabel={walletOptionLabel}
              getValue={(item) => item.id}
              placeholder="ຄົ້ນຫາ ຫຼື ເລື່ອກກະເປົາ (ຮ້ານ/ຊ່າງ)..."
              clearable
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <FormPrice
                name="amount"
                requiredMark
                label="ຈຳນວນເງິນ"
                placeholder="0"
                currencySymbol="K"
              />
              <FormSelect
                name="currency"
                label="ສະກຸນເງິນ"
                options={CURRENCY_OPTIONS}
              />
            </div>

            <FormSelect
              name="payoutMethod"
              label="ວິທີຈ່າຍ"
              options={PAYOUT_METHOD_OPTIONS}
            />

            <FormTextarea
              name="payoutDestinationJson"
              label="ລາຍລະອຽດປາຍທາງ (JSON, ບໍ່ບັງຄັບ)"
              placeholder='{"bankName": "...", "accountNo": "..."}'
              rows={3}
              className="resize-none"
            />

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => nav({ to: "/app/wallet/payouts" })}
              >
                ຍົກເລີກ
              </Button>
              <Button type="submit" isLoading={createMutation.isPending}>
                ບັນທຶກ
              </Button>
            </div>
          </FormRoot>
        </div>
      </Main>
    </>
  );
}
